import {
  Drawer,
  DrawerClose,
  DrawerContent,
  DrawerFooter,
  DrawerHeader,
  DrawerTitle,
  DrawerDescription,
  DrawerTrigger,
} from '~/components/ui/drawer';

import { ChevronLeft } from 'lucide-react';
import { Input } from '~/components/ui/input';
import { Label } from '~/components/ui/label';
import { useRef, useState, useEffect } from 'react';
import { Transition } from '@headlessui/react';
import clsx from 'clsx';
import { AreaForm } from '../form/area-form';
import { MapAreaItem } from '~/lib/map-area-prefectures';
import { LicenseForm } from '../form/license-form';
import { CategoryForm } from '../form/category-form';
import type { MasterData, ParentItem } from '~/types/master-data';
import { TypeForm } from '../form/type-form';
import { AnnualSalaryForm } from '../form/annual-salary-form';
import { ContractForm } from '../form/contract-form';
import { FeatureForm } from '../form/feature-form';
import { useRouter } from 'next/navigation';
import { generatePath } from '~/lib/generate-path';
import type { SearchConditions } from '~/types/search-conditions';
type InputModeType = 'prefectures' | 'licenses' | null;

interface JobSearchDrawerProps {
  masterData: MasterData;
  categoryNamesSet: Set<string>;
  mappedAreas: MapAreaItem[];
  mappedLicenses: ParentItem[];
  searchConditions: SearchConditions;
  updateParentChecked: (
    item: ParentItem,
    type: keyof SearchConditions,
    checked: boolean,
  ) => void;
  updateChecked: (
    type: keyof SearchConditions,
    name: string,
    checked: boolean,
  ) => void;
  setSearchConditions: (conditions: SearchConditions) => void;
  resetConditions: () => void;
}

export function JobSearchDrawer({
  masterData,
  categoryNamesSet,
  mappedAreas,
  mappedLicenses,
  searchConditions,
  setSearchConditions,
  updateParentChecked,
  updateChecked,
  resetConditions,
}: JobSearchDrawerProps) {
  const [inputModeType, setInputModeType] = useState<InputModeType>(null);
  const scrollableRef = useRef<HTMLDivElement>(null);
  const router = useRouter();
  const searchHandler = () => {
    const path = generatePath(searchConditions, masterData);
    router.push(path);
  };

  // モーダル内で横に遷移するたびにトップに戻るロジック
  useEffect(() => {
    if (scrollableRef.current) {
      scrollableRef.current.scrollTo({ top: 0 });
    }
  }, [inputModeType]);

  return (
    <Drawer>
      <DrawerTrigger asChild>
        <div className="py-2 flex justify-center md:hidden">
          <button
            type="button"
            className="c-button c-button-cta -search c-icon -checked -icon-md"
          >
            条件を変更して検索する
          </button>
        </div>
      </DrawerTrigger>
      <DrawerContent
        className="h-[calc(100%-46px)] overflow-clip"
        hideIndicator
      >
        <div className="mx-auto w-full flex flex-col h-full">
          <DrawerDescription className="hidden">
            検索条件を設定するためのダイアログです。
          </DrawerDescription>
          <DrawerHeader className="flex relative p-0">
            <div className="p-sp_search_menu__header">
              {inputModeType === null && (
                <DrawerClose asChild>
                  <button
                    className="p-sp_search_menu__header__close"
                    type="button"
                  >
                    <ChevronLeft />
                  </button>
                </DrawerClose>
              )}
              {inputModeType !== null && (
                <button
                  className="p-sp_search_menu__header__close"
                  type="button"
                  onClick={() => setInputModeType(null)}
                >
                  <ChevronLeft />
                </button>
              )}

              <DrawerTitle className="p-sp_search_menu__header__title">
                {inputModeType === null && '検索条件'}
                {inputModeType === 'prefectures' && 'エリア'}
                {inputModeType === 'licenses' && '資格'}
              </DrawerTitle>
            </div>
          </DrawerHeader>

          <div className="flex-1 overflow-y-auto relative" ref={scrollableRef}>
            <Transition show={inputModeType === null}>
              <div
                className={clsx(
                  'p-4 flex flex-col',
                  'absolute inset-0 bg-white transition duration-300',
                  'data-[closed]:-translate-x-full data-[closed]:opacity-0',
                  'data-[open]:translate-x-0 data-[open]:opacity-100',
                )}
              >
                <div className="flex flex-col gap-4">
                  <div className="flex flex-col gap-2">
                    <Label htmlFor="free-word">フリーワード</Label>
                    <Input
                      id="free-word"
                      placeholder="フリーワード"
                      value={searchConditions.freeWord}
                      onChange={(e) =>
                        setSearchConditions({
                          ...searchConditions,
                          freeWord: e.target.value,
                        })
                      }
                    />
                  </div>
                  <div className="flex flex-col gap-2">
                    <Label htmlFor="prefecture">エリア</Label>
                    <Input
                      id="prefecture"
                      placeholder="エリア"
                      readOnly
                      value={searchConditions.prefectureNames.join(',')}
                      onClick={() => setInputModeType('prefectures')}
                      className="truncate"
                    />
                  </div>
                  <div className="flex flex-col gap-2">
                    <Label htmlFor="license">資格</Label>
                    <Input
                      id="license"
                      placeholder="資格"
                      readOnly
                      value={searchConditions.licenseNames.join(',')}
                      onClick={() => setInputModeType('licenses')}
                      className="truncate"
                    />
                  </div>
                  <div className="flex justify-center border-b pb-4">
                    <button
                      type="button"
                      className="c-button -green"
                      onClick={searchHandler}
                    >
                      検索
                    </button>
                  </div>
                </div>
                <CategoryForm
                  masterData={masterData}
                  categoryNamesSet={categoryNamesSet}
                  updateChecked={updateChecked}
                />
                <TypeForm
                  masterData={masterData}
                  searchConditions={searchConditions}
                  updateChecked={updateChecked}
                />
                <AnnualSalaryForm
                  searchConditions={searchConditions}
                  setSearchConditions={setSearchConditions}
                />
                <ContractForm
                  masterData={masterData}
                  searchConditions={searchConditions}
                  updateChecked={updateChecked}
                />
                <FeatureForm
                  masterData={masterData}
                  searchConditions={searchConditions}
                  updateChecked={updateChecked}
                />
                {inputModeType === null && (
                  <div className="flex justify-center items-center pb-2">
                    <span
                      className="c-button -lightgray -small u-mr15"
                      onClick={resetConditions}
                    >
                      クリア
                    </span>

                    <button
                      type="button"
                      className="c-button -green"
                      onClick={searchHandler}
                    >
                      検索
                    </button>
                  </div>
                )}
              </div>
            </Transition>
            <Transition show={inputModeType === 'prefectures'}>
              <div
                className={clsx(
                  'absolute inset-0 bg-white transition duration-300',
                  'data-[closed]:translate-x-full data-[closed]:opacity-0',
                  'data-[open]:translate-x-0 data-[open]:opacity-100',
                )}
              >
                <AreaForm
                  mappedAreas={mappedAreas}
                  searchConditions={searchConditions}
                  updateParentChecked={updateParentChecked}
                  updateChecked={updateChecked}
                />
              </div>
            </Transition>
            <Transition show={inputModeType === 'licenses'}>
              <div
                className={clsx(
                  'absolute inset-0 bg-white transition duration-300',
                  'data-[closed]:translate-x-full data-[closed]:opacity-0',
                  'data-[open]:translate-x-0 data-[open]:opacity-100',
                )}
              >
                <LicenseForm
                  mappedLicenses={mappedLicenses}
                  searchConditions={searchConditions}
                  updateParentChecked={updateParentChecked}
                  updateChecked={updateChecked}
                />
              </div>
            </Transition>
          </div>
          {inputModeType !== null && (
            <DrawerFooter className="justify-center bg-black bg-opacity-50 py-2">
              <div className="flex justify-center items-center gap-2">
                <button
                  type="button"
                  className="c-button -blue -large"
                  onClick={() => setInputModeType(null)}
                >
                  他の条件を変更
                </button>

                <button
                  type="button"
                  className="c-button -green -large"
                  onClick={searchHandler}
                >
                  検索
                </button>
              </div>
            </DrawerFooter>
          )}
        </div>
      </DrawerContent>
    </Drawer>
  );
}
